import type { FC } from 'react';
import { useTranslation } from 'next-i18next';
import type { HomepageListing } from 'src/common/types/api/v4/search/v1/homepage';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import Typography from 'src/common/components/Typography';
import ListingCard from 'src/common/components/ListingCard';
import Button from 'src/common/components/ButtonDS22';
import { PATHS } from 'src/common/constants';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';

import GulperImpressions from 'src/common/components/GulperImpressions';
import styles from './DiscoverBoats.module.scss';

type DiscoverBoatsProps = {
  isExpandedSearch: boolean;
  listings: HomepageListing[];
};

const DiscoverBoats: FC<DiscoverBoatsProps> = ({ isExpandedSearch, listings }) => {
  const [t] = useTranslation('home-page');
  const title = isExpandedSearch ? t('discover-boat-rentals') : t('discover-boats-near-you');

  const { isMobileNativeWebView } = useMobileNativeWebView();
  if (isMobileNativeWebView) {
    return null;
  }

  return (
    <PitchPageLayout backgroundColor="bluminescence100">
      <div className={styles.root}>
        <div className={styles.title}>
          <Typography
            type="h2"
            size="display-5"
            sizeFromBreakpointM="display-4"
          >
            {title}
          </Typography>
        </div>

        <div
          className={styles.listingsWrapper}
          data-gulper-section="Search"
        >
          {listings.map((listing, index) => (
            <ListingCard
              key={listing.id}
              listingId={listing.id}
              listingUrl={listing.listing_url}
              headline={listing.headline}
              breadcrumbs={listing.breadcrumbs}
              category={listing.cat_loc_breadcrumb}
              rating={listing.review_average ? parseFloat(listing.review_average) : undefined}
              awards={listing.user_details.awards}
              isSuperOwner={listing.user_details.is_super_owner}
              instantBookingAvailable={listing.has_available_instabook && listing.instabook_enabled}
              searchPricing={listing.search_price_nested}
              imageSrc={listing.photo_url ?? ''}
              capacity={t('listings-capacity', { count: listing.capacity })}
              snoozeStartDate={listing?.snooze_start_date ?? undefined}
              snoozeEndDate={listing?.snooze_end_date ?? undefined}
              variant="homepage"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          ))}
          <GulperImpressions data={listings} />
        </div>

        {!isExpandedSearch && (
          <div className={styles.button}>
            <Button
              type="link"
              href={`${PATHS.SEARCH}`}
              variant="tertiary"
              size="large"
            >
              <Typography
                size="body-l"
                type="p"
                weight="semibold"
              >
                {t('view-more')}
              </Typography>
            </Button>
          </div>
        )}
      </div>
    </PitchPageLayout>
  );
};

export default DiscoverBoats;
